import { Sparkles, Lock, Clock, Brain, Bot, Stars, MessageCircle, FileText, Undo2, Download, TrendingUp, Zap, BarChart3, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const FeatureCard = ({ icon: Icon, title, description, isUpcoming, isHighlighted, gradient }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02, y: -4 }}
    className={`p-6 rounded-xl backdrop-blur-xl border relative overflow-hidden transition-all duration-300 ${
      isHighlighted
        ? "border-green-500/50 bg-gradient-to-br from-green-500/10 to-emerald-500/10 shadow-lg shadow-green-500/10"
        : isUpcoming
        ? "border-gray-700/50 bg-gray-800/30"
        : "border-blue-500/20 bg-blue-900/10"
    }`}
  >
    {isHighlighted && (
      <div className="absolute top-0 right-0 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-semibold rounded-bl-lg">
        ✨ NEW
      </div>
    )}
    <div className="flex items-start space-x-4">
      <div
        className={`p-2.5 rounded-xl ${
          isHighlighted
            ? "bg-gradient-to-r from-green-500 to-emerald-500"
            : isUpcoming
            ? "bg-gray-700"
            : gradient || "bg-gradient-to-r from-blue-500 to-cyan-400"
        }`}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className={`text-lg font-semibold ${isHighlighted ? 'text-green-100' : 'text-white'}`}>{title}</h3>
          {isUpcoming && (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-300">
              Coming Soon
            </span>
          )}
        </div>
        <p className={`mt-1 ${isHighlighted ? 'text-gray-300' : 'text-gray-400'}`}>{description}</p>
      </div>
    </div>
  </motion.div>
);

const SubFeatureItem = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 text-sm text-gray-400">
    <Icon className="w-4 h-4 text-green-400" />
    <span>{text}</span>
  </div>
);

export const FeaturesPage = () => {
  const currentFeatures = [
    {
      icon: Bot,
      title: "AI-Powered Interviews",
      description:
        "Dynamic interview sessions tailored to your role and experience level with realistic AI-driven questions.",
      gradient: "bg-gradient-to-r from-blue-500 to-cyan-400"
    },
    {
      icon: BarChart3,
      title: "Performance Analytics",
      description:
        "Get detailed insights into your interview performance with comprehensive scoring and feedback.",
      gradient: "bg-gradient-to-r from-purple-500 to-pink-500"
    },
  ];

  const resumeChatFeatures = [
    { icon: CheckCircle2, text: "Real-time resume updates as you chat" },
    { icon: TrendingUp, text: "ATS score & analytics tracking" },
    { icon: Undo2, text: "Version history with one-click rollback" },
    { icon: Download, text: "Export to PDF instantly" },
    { icon: Sparkles, text: "AI-powered content suggestions" },
    { icon: FileText, text: "Highlighted changes for easy review" },
  ];

  const upcomingFeatures = [
    {
      icon: Brain,
      title: "Smart Feedback",
      description:
        "Receive detailed feedback on your responses and areas for improvement.",
    },
    {
      icon: Clock,
      title: "Real-time Analysis",
      description:
        "Get instant feedback on your interview performance and communication skills.",
    },
    {
      icon: Sparkles,
      title: "Multi-language Support",
      description:
        "Practice interviews in multiple programming languages and human languages.",
    },
    {
      icon: Lock,
      title: "Interview Recording",
      description:
        "Record and replay your interviews for better self-assessment.",
    },
    {
      icon: Stars,
      title: "Custom Interview Paths",
      description:
        "Create personalized interview tracks based on your career goals.",
    },
  ];

  return (
    <div className="h-full overflow-auto px-4 py-8">
      <Helmet>
        <title>Features - Sakshatkar AI</title>
        <meta
          name="description"
          content="Explore the current and upcoming features of Sakshatkar AI, including AI-powered interviews and the new Resume Chat feature."
        />
        <meta property="og:title" content="Sakshatkar AI Features" />
      </Helmet>
      <div className="max-w-5xl mx-auto space-y-16">
        {/* Hero Feature - Resume Chat */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-cyan-500/20 rounded-3xl blur-xl" />
          <div className="relative p-8 rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-green-500/30 backdrop-blur-xl">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
                  <Sparkles className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-green-300">New Feature</span>
                </div>
                
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                    Resume Chat
                  </h2>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Transform your resume through conversation. Chat with AI to enhance, 
                    optimize, and perfect your resume in real-time. See updates instantly, 
                    track your progress, and download when ready.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {resumeChatFeatures.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + idx * 0.1 }}
                    >
                      <SubFeatureItem {...feature} />
                    </motion.div>
                  ))}
                </div>

                <motion.a
                  href={process.env.REACT_APP_FRONTEND_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/25 transition-all duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  Try Resume Chat Now
                </motion.a>
              </div>

              {/* Visual Representation */}
              <div className="lg:w-80 flex-shrink-0">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 to-emerald-500/30 rounded-2xl blur-xl" />
                  <div className="relative p-6 rounded-2xl bg-gray-900/90 border border-gray-700/50">
                    {/* Mock Chat Interface */}
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="px-3 py-2 bg-gray-700/50 rounded-lg rounded-tl-none text-sm text-gray-300">
                          How can I help improve your resume today?
                        </div>
                      </div>
                      <div className="flex items-start gap-2 justify-end">
                        <div className="px-3 py-2 bg-green-500/20 border border-green-500/30 rounded-lg rounded-tr-none text-sm text-gray-200">
                          Add Python to my skills
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="px-3 py-2 bg-gray-700/50 rounded-lg rounded-tl-none text-sm text-gray-300">
                          ✅ Added Python to your skills section!
                        </div>
                      </div>
                    </div>
                    
                    {/* Mock Resume Preview */}
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="text-xs text-gray-400">Live Preview</span>
                        <span className="text-xs text-green-400 ml-auto">v3</span>
                      </div>
                      <div className="p-2 bg-white/5 rounded-lg">
                        <div className="h-2 w-24 bg-gray-600 rounded mb-2" />
                        <div className="h-1.5 w-full bg-gray-700 rounded mb-1" />
                        <div className="h-1.5 w-3/4 bg-gray-700 rounded mb-2" />
                        <div className="flex gap-1">
                          <span className="px-1.5 py-0.5 text-[8px] bg-green-500/30 text-green-300 rounded animate-pulse">Python</span>
                          <span className="px-1.5 py-0.5 text-[8px] bg-blue-500/30 text-blue-300 rounded">JavaScript</span>
                          <span className="px-1.5 py-0.5 text-[8px] bg-purple-500/30 text-purple-300 rounded">React</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Current Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-6"
        >
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold text-white">Current Features</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {currentFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + idx * 0.1 }}
              >
                <FeatureCard {...feature} />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Upcoming Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="space-y-6"
        >
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-gray-400" />
            <h2 className="text-2xl font-bold text-white">In the Pipeline</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {upcomingFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + idx * 0.1 }}
              >
                <FeatureCard {...feature} isUpcoming />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
